import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, XCircle, AlertTriangle, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import UnifiedContactModal from '../components/UnifiedContactModal';

const ManualCleaningPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Manual Cleaning for Inline Filters | BIG, BIG-S, GNG | CTC Waterworks"
        description="Manual cleaning approach for BIG, BIG-S, and GNG inline filters. Budget-friendly option for lower duty cycle operations with standard disassembly maintenance."
        canonicalPath="/filtration/inline/cleaning/manual"
      />
      <Navbar />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline" className="text-gray-600 hover:text-ctc-blue">Inline Filters</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline/cleaning" className="text-gray-600 hover:text-ctc-blue">Cleaning Options</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Manual</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Manual Cleaning
            </h1>
            <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
              Traditional disassembly-based maintenance for budget-conscious operations with lower duty cycles.
            </p>

            <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                Manual cleaning is the most economical cleaning approach, requiring no additional automation hardware. Filter operation is suspended, the housing is disassembled, and the screen element is removed for external cleaning before reassembly.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Manual Cleaning Is */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Manual Cleaning Process</h2>

          <div className="space-y-6">
            <div className="flex items-start">
              <div className="bg-ctc-blue text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">1</div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Shut Down & Isolate</h3>
                <p className="text-gray-700 leading-relaxed">
                  Stop flow, close isolation valves, and depressurize the filter housing.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="bg-ctc-blue text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">2</div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Remove Head/Cover</h3>
                <p className="text-gray-700 leading-relaxed">
                  Unbolt or unclamp the filter head to access the internal screen element.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="bg-ctc-blue text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">3</div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Extract Screen</h3>
                <p className="text-gray-700 leading-relaxed">
                  Lift the screen element out of the filter body for cleaning.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="bg-ctc-blue text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">4</div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Clean Screen</h3>
                <p className="text-gray-700 leading-relaxed">
                  Spray or brush debris from screen openings using water, air, or approved cleaning agents.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="bg-ctc-blue text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">5</div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Inspect & Reassemble</h3>
                <p className="text-gray-700 leading-relaxed">
                  Check screen for damage, replace gaskets if needed, reinstall screen, and secure head.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="bg-ctc-blue text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">6</div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Return to Service</h3>
                <p className="text-gray-700 leading-relaxed">
                  Open valves, restart flow, and verify proper operation with no leaks.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pros & Cons */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Advantages & Limitations</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Pros */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-green-600 mb-6 flex items-center">
                <CheckCircle className="mr-3" size={28} />
                Advantages
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Lowest upfront cost—no automation hardware required</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Thorough visual inspection during each cleaning cycle</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Simple maintenance with standard hand tools</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">No electrical or control system dependencies</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Suitable for low-duty cycle or seasonal operations</span>
                </li>
              </ul>
            </div>

            {/* Cons */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-red-600 mb-6 flex items-center">
                <XCircle className="mr-3" size={28} />
                Limitations
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">High downtime—typically 30-60 minutes per cleaning</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Labor intensive—requires operator presence and physical work</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Not suitable for 24/7 or high-frequency cleaning operations</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Repeated gasket removal may increase wear and replacement frequency</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Flow interruption affects downstream processes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Best For */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Manual Cleaning Is Best For:</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Budget-Sensitive Projects:</span> Initial equipment cost is prioritized over operational efficiency.
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Low Duty Cycle:</span> Filter cleaning is infrequent (weekly or less) and scheduled during planned downtime.
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Clean Water Sources:</span> Low solids loading with minimal debris accumulation.
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Seasonal Operations:</span> Equipment is only used part of the year with long idle periods.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upgrade Path Banner */}
      <section className="py-12 bg-gradient-to-r from-ctc-blue to-blue-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <TrendingUp className="text-white mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold text-white mb-4">Reduce Downtime with Semi-Automatic Cleaning</h2>
          <p className="text-xl text-blue-100 mb-8">
            Upgrade to BIF & MVC semi-automatic cleaning to reduce cleaning time from 30-60 minutes down to 5-15 minutes—without full teardown.
          </p>
          <Link
            to="/filtration/inline/cleaning/semi-auto"
            className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center shadow-lg"
          >
            <span>Explore Semi-Automatic Options</span>
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* Compatible Series */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Manual Cleaning Compatible Series</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/filtration/inline/big"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-gray-200 hover:border-ctc-blue"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">BIG Series</h3>
              <p className="text-gray-700 text-sm mb-4">
                Precision gasket-sealed filters with manual disassembly for thorough cleaning.
              </p>
              <div className="text-ctc-blue font-semibold flex items-center">
                View BIG Series
                <ArrowRight className="ml-2" size={16} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/big-s"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-gray-200 hover:border-gray-600"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">BIG-S Series</h3>
              <p className="text-gray-700 text-sm mb-4">
                Stainless steel inline filters with modular ports and manual maintenance access.
              </p>
              <div className="text-gray-600 font-semibold flex items-center">
                View BIG-S Series
                <ArrowRight className="ml-2" size={16} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/gng"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-gray-200 hover:border-green-600"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">GNG Series</h3>
              <p className="text-gray-700 text-sm mb-4">
                Economy filters with ring-lock quick-release head for fast screen removal.
              </p>
              <div className="text-green-600 font-semibold flex items-center">
                View GNG Series
                <ArrowRight className="ml-2" size={16} />
              </div>
            </Link>
          </div>

          {/* TIG Notice */}
          <div className="mt-8 bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
            <div className="flex items-start">
              <AlertTriangle className="text-orange-600 mr-3 mt-1 flex-shrink-0" size={24} />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">TIG Series Note</h4>
                <p className="text-gray-700 leading-relaxed">
                  TIG filters do not support Brush/Vacuum/RAB cleaning options. Follow standard TIG maintenance procedures only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Need Help Selecting a Cleaning Option?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Our filtration specialists can help you evaluate manual vs. semi-automatic vs. fully automatic cleaning based on your operational requirements.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-ctc-blue text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <span>Talk to a Filtration Specialist</span>
              <ArrowRight className="ml-2" size={20} />
            </button>

            <Link
              to="/filtration/inline/cleaning"
              className="border-2 border-ctc-blue text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-ctc-blue hover:text-white transition-colors inline-flex items-center justify-center"
            >
              Compare All Options
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="product_quote"
      />
    </div>
  );
};

export default ManualCleaningPage;
